import type { BilingualLabel } from '../../components/Bilingual'
import type { RingReason } from '../../lib/ring-api'

// R503 (b): the ring's labels, one file so Rook's Arabic is a one-file carry (the kitchen-i18n
// pattern). Every screen shows them as a Bilingual pair. The Arabic values are Rook's, copied
// byte for byte from his vault file (f103db578, section "R503 (b) the HOP ring module").

export type RingLabelKey =
  | 'ringTitle'
  | 'ringArm'
  | 'ringArming'
  | 'ringArmHint'
  | 'ringBlocked'
  | 'ringChecking'
  | 'ringFailed'
  | 'ringNone'
  | 'ringOff'
  | 'ringQuiet'
  | 'ringHeard'
  | 'ringPaused'
  | 'ringAgeSeconds'
  | 'ringAgeMinutes'
  | 'ringWhoPlate'
  | 'reasonPending'
  | 'reasonHeldCard'
  | 'reasonKitchenNotStarted'
  | 'reasonFinalCheckNotStarted'

export const RING_LABEL: Record<RingLabelKey, BilingualLabel & { ar: string | null }> = {
  ringTitle: { en: 'Ringing now', ar: 'يرنّ الآن' },
  ringArm: { en: 'Turn on the ring', ar: 'تشغيل الرنّة' },
  ringArming: { en: 'Turning on\u2026', ar: 'جارٍ التشغيل…' },
  ringArmHint: { en: 'The ring needs one tap on this device before it can make a sound.', ar: 'تحتاج الرنّة إلى نقرة واحدة على هذا الجهاز قبل أن تُصدر صوتًا.' },
  ringBlocked: { en: 'Sound is still blocked. Tap again.', ar: 'الصوت ما زال محجوبًا. انقر مجددًا.' },
  ringChecking: { en: 'Checking for waiting orders\u2026', ar: 'جارٍ التحقق من الطلبات المنتظرة…' },
  ringFailed: { en: "Couldn't check for waiting orders. Retrying.", ar: 'تعذّر التحقق من الطلبات المنتظرة. تجري إعادة المحاولة.' },
  ringNone: { en: 'No order is waiting.', ar: 'لا يوجد طلب منتظر.' },
  ringOff: { en: 'The ring is off for this screen.', ar: 'الرنّة متوقّفة على هذه الشاشة.' },
  ringQuiet: { en: 'Quiet hours: the ring makes no sound.', ar: 'ساعات الهدوء: الرنّة بلا صوت.' },
  ringHeard: { en: 'Heard it', ar: 'سمعته' },
  // {time} is filled by the panel (hh:mm on this device's clock).
  ringPaused: { en: 'Paused on this device until {time}', ar: 'متوقّفة على هذا الجهاز حتى {time}' },
  // {n} is filled by the panel.
  ringAgeSeconds: { en: '{n} s', ar: '{n} ث' },
  ringAgeMinutes: { en: '{n} min', ar: '{n} د' },
  // Rook #67688: the person and the plate lead; {name} is the customer's name and {plate} the
  // first one or two item names joined by ", ", both as the server sends them.
  ringWhoPlate: { en: "{name}'s {plate}", ar: '{plate} لـ{name}' },
  reasonPending: { en: 'Waiting to be confirmed', ar: 'بانتظار التأكيد' },
  reasonHeldCard: { en: 'Card open on the Cockpit', ar: 'بطاقة مفتوحة في لوحة القيادة' },
  reasonKitchenNotStarted: { en: 'Kitchen has not started', ar: 'المطبخ لم يبدأ' },
  reasonFinalCheckNotStarted: { en: 'Final check not started', ar: 'الفحص النهائي لم يبدأ' },
}

export const REASON_LABEL: Record<RingReason, RingLabelKey> = {
  pending: 'reasonPending',
  held_card: 'reasonHeldCard',
  kitchen_not_started: 'reasonKitchenNotStarted',
  final_check_not_started: 'reasonFinalCheckNotStarted',
}

/** A label with its `{slot}`s filled, in both languages. */
export function ringLabel(key: RingLabelKey, slots?: Record<string, string>): BilingualLabel {
  const pair = RING_LABEL[key]
  const fill = (s: string) => (slots ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in slots ? slots[k] : m)) : s)
  return { en: fill(pair.en), ar: pair.ar === null ? null : fill(pair.ar) }
}
